import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
	AlertTriangle,
	ArchiveRestore,
	ChevronLeft,
	ChevronRight,
	Dumbbell,
	ExternalLink,
	Layers3,
	Search,
	X,
} from 'lucide-react'
import { FormEvent, KeyboardEvent, useEffect, useMemo, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'

import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { useAdminApi } from '@app/api/api-client-context'
import { useAuth } from '@app/auth/auth-context'
import { exerciseKeys, type Exercise } from '@entities/exercise'
import {
	getProgramStructure,
	programKeys,
	programLifecycleLabel,
	type Program,
} from '@entities/program'
import {
	AdminApiError,
	createIdempotencyKey,
	getNextCursor,
	unwrapApiResponse,
} from '@shared/api'
import { getInterfaceErrorDescription, interfaceMessages } from '@shared/i18n/interface-messages'
import { ConfirmDialog } from '@shared/ui/confirm-dialog'
import { EmptyState, ErrorState, LoadingState, PageHeader } from '@shared/ui/page'
import { useToast } from '@shared/ui/toast'
import {
	parseArchiveFilters,
	serializeArchiveFilters,
	type ArchiveFilters,
	type ArchiveKind,
} from '../model/filters'

type RestoreTarget =
	| { kind: 'exercises'; item: Exercise }
	| { kind: 'programs'; item: Program }

type ExerciseProgramReferences = {
	count: number
	programs: Array<{ id: string; name: string; lifecycle: Program['lifecycle'] }>
}

const formatDate = (value?: string) => value
	? new Intl.DateTimeFormat('ru-RU', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value))
	: '—'

const programReferenceSummary = (program: Program) => {
	const workouts = new Set<string>()
	const exercises = new Set<string>()
	for (const week of program.weeks) {
		for (const day of week.days) {
			if (day.workout_template_id) workouts.add(day.workout_template_id)
			for (const exercise of day.exercises ?? []) exercises.add(exercise.exercise_id)
		}
	}
	return { workouts: workouts.size, exercises: exercises.size }
}

const targetName = (target: RestoreTarget) => target.item.name

export const ArchivePage = () => {
	const api = useAdminApi()
	const { can } = useAuth()
	const history = useHistory()
	const location = useLocation()
	const queryClient = useQueryClient()
	const { notify } = useToast()
	const filters = useMemo(() => parseArchiveFilters(location.search), [location.search])
	const [searchDraft, setSearchDraft] = useState(filters.search)
	const [cursor, setCursor] = useState<string | null>(null)
	const [cursorHistory, setCursorHistory] = useState<Array<string | null>>([])
	const [restoreTarget, setRestoreTarget] = useState<RestoreTarget | null>(null)
	const [conflictMessage, setConflictMessage] = useState<string | null>(null)
	const attemptKey = useRef(createIdempotencyKey())
	const exerciseTabRef = useRef<HTMLButtonElement>(null)
	const programTabRef = useRef<HTMLButtonElement>(null)

	useEffect(() => {
		setSearchDraft(filters.search)
		setCursor(null)
		setCursorHistory([])
	}, [filters.kind, filters.search, filters.sort, filters.direction, filters.limit])

	const queryFilters = useMemo(() => ({ ...filters, cursor, lifecycle: 'ARCHIVED' }), [cursor, filters])
	const exercises = useQuery({
		queryKey: exerciseKeys.list(queryFilters),
		queryFn: async () => unwrapApiResponse(await api.GET('/admin/v1/exercises', {
			params: { query: {
				cursor: cursor ?? undefined,
				limit: filters.limit,
				search: filters.search || undefined,
				lifecycle: 'ARCHIVED',
				sort: filters.sort,
				direction: filters.direction,
			} },
		})).data,
		enabled: filters.kind === 'exercises',
		placeholderData: previous => previous,
	})
	const programs = useQuery({
		queryKey: programKeys.list(queryFilters),
		queryFn: async () => unwrapApiResponse(await api.GET('/admin/v1/programs', {
			params: { query: {
				cursor: cursor ?? undefined,
				limit: filters.limit,
				q: filters.search || undefined,
				lifecycle: 'ARCHIVED',
				sort: filters.sort,
				direction: filters.direction,
			} },
		})).data,
		enabled: filters.kind === 'programs',
		placeholderData: previous => previous,
	})

	const exerciseReferences = useQuery({
		queryKey: ['admin', 'archive', 'exercise-program-references', restoreTarget?.kind === 'exercises' ? restoreTarget.item.id : 'none'],
		enabled: restoreTarget?.kind === 'exercises',
		queryFn: async (): Promise<ExerciseProgramReferences> => {
			if (!restoreTarget || restoreTarget.kind !== 'exercises') return { count: 0, programs: [] }
			const references: ExerciseProgramReferences['programs'] = []
			let nextCursor: string | undefined
			for (let pageIndex = 0; pageIndex < 100; pageIndex += 1) {
				const page = unwrapApiResponse(await api.GET('/admin/v1/programs', {
					params: { query: {
						cursor: nextCursor,
						limit: 100,
						sort: 'id',
						direction: 'asc',
					} },
				})).data
				for (const program of page.items) {
					const referenced = program.weeks.some(week => week.days.some(day =>
						(day.exercises ?? []).some(exercise => exercise.exercise_id === restoreTarget.item.id),
					))
					if (referenced) references.push({ id: program.id, name: program.name, lifecycle: program.lifecycle })
				}
				nextCursor = getNextCursor(page.page) ?? undefined
				if (!nextCursor) break
			}
			return { count: references.length, programs: references }
		},
		staleTime: 30_000,
	})

	const activeQuery = filters.kind === 'exercises' ? exercises : programs
	const activePage = filters.kind === 'exercises' ? exercises.data?.page : programs.data?.page
	const updateFilters = (patch: Partial<ArchiveFilters>) => {
		const next = { ...filters, ...patch }
		history.replace({ pathname: location.pathname, search: serializeArchiveFilters(next) })
	}
	const changeKind = (kind: ArchiveKind) => updateFilters({ kind })
	const handleTabKeyDown = (event: KeyboardEvent<HTMLButtonElement>, kind: ArchiveKind) => {
		const nextKind = event.key === 'Home' || event.key === 'ArrowLeft' || event.key === 'ArrowUp'
			? 'exercises'
			: event.key === 'End' || event.key === 'ArrowRight' || event.key === 'ArrowDown'
				? 'programs'
				: null
		if (!nextKind || nextKind === kind) return
		event.preventDefault()
		changeKind(nextKind)
		requestAnimationFrame(() => (nextKind === 'exercises' ? exerciseTabRef : programTabRef).current?.focus())
	}
	const submitSearch = (event: FormEvent) => {
		event.preventDefault()
		updateFilters({ search: searchDraft })
	}
	const resetFilters = () => {
		setSearchDraft('')
		history.replace({ pathname: location.pathname, search: serializeArchiveFilters({ ...filters, search: '', sort: 'updated_at', direction: 'desc', limit: 25 }) })
	}
	const goNext = () => {
		const next = getNextCursor(activePage)
		if (!next) return
		setCursorHistory(current => [...current, cursor])
		setCursor(next)
	}
	const goPrevious = () => {
		setCursorHistory(current => {
			if (!current.length) return current
			setCursor(current[current.length - 1])
			return current.slice(0, -1)
		})
	}
	const openRestore = (target: RestoreTarget) => {
		attemptKey.current = createIdempotencyKey()
		setConflictMessage(null)
		setRestoreTarget(target)
	}
	const closeRestore = () => {
		if (restoreMutation.isPending) return
		setRestoreTarget(null)
		setConflictMessage(null)
	}

	const restoreMutation = useMutation({
		mutationFn: async () => {
			if (!restoreTarget) throw new Error('Объект восстановления не выбран.')
			const header = { 'Idempotency-Key': attemptKey.current, 'If-Match': restoreTarget.item.etag }
			if (restoreTarget.kind === 'exercises') {
				return {
					kind: restoreTarget.kind,
					data: unwrapApiResponse(await api.POST('/admin/v1/exercises/{exercise_id}/restore', {
						params: { path: { exercise_id: restoreTarget.item.id }, header },
					})).data,
				}
			}
			return {
				kind: restoreTarget.kind,
				data: unwrapApiResponse(await api.POST('/admin/v1/programs/{program_id}/restore', {
					params: { path: { program_id: restoreTarget.item.id }, header },
				})).data,
			}
		},
		onSuccess: async result => {
			attemptKey.current = createIdempotencyKey()
			if (result.kind === 'exercises') await queryClient.invalidateQueries({ queryKey: exerciseKeys.all })
			else await queryClient.invalidateQueries({ queryKey: programKeys.all })
			notify({ title: 'Объект восстановлен', description: `${result.data.name} возвращён в черновики.`, tone: 'success' })
			setRestoreTarget(null)
			setConflictMessage(null)
		},
		onError: async error => {
			if (restoreTarget && error instanceof AdminApiError && (error.status === 409 || error.status === 412)) {
				try {
					if (restoreTarget.kind === 'exercises') {
						const latest = unwrapApiResponse(await api.GET('/admin/v1/exercises/{exercise_id}', {
							params: { path: { exercise_id: restoreTarget.item.id } },
						})).data
						if (latest.lifecycle !== 'ARCHIVED') {
							setRestoreTarget(null)
							await queryClient.invalidateQueries({ queryKey: exerciseKeys.all })
							notify({ title: 'Состояние уже изменилось', description: `${latest.name} больше не находится в архиве.`, tone: 'success' })
							return
						}
						setRestoreTarget({ kind: 'exercises', item: latest })
					} else {
						const latest = unwrapApiResponse(await api.GET('/admin/v1/programs/{program_id}', {
							params: { path: { program_id: restoreTarget.item.id } },
						})).data
						if (latest.lifecycle !== 'ARCHIVED') {
							setRestoreTarget(null)
							await queryClient.invalidateQueries({ queryKey: programKeys.all })
							notify({ title: 'Состояние уже изменилось', description: `${latest.name} больше не находится в архиве.`, tone: 'success' })
							return
						}
						setRestoreTarget({ kind: 'programs', item: latest })
					}
					setConflictMessage('Серверная версия изменилась. Данные и ETag обновлены; проверьте предупреждения и повторите восстановление.')
					return
				} catch {
					setConflictMessage('Не удалось загрузить актуальную версию. Закройте окно и повторите операцию из обновлённого списка.')
					return
				}
			}
			notify({ title: 'Не удалось восстановить объект', description: getInterfaceErrorDescription(error), tone: 'error' })
		},
	})

	const hasFilters = Boolean(filters.search || filters.sort !== 'updated_at' || filters.direction !== 'desc' || filters.limit !== 25)
	const count = filters.kind === 'exercises' ? exercises.data?.items.length ?? 0 : programs.data?.items.length ?? 0

	return (
		<section className='space-y-6'>
			<PageHeader
				eyebrow={interfaceMessages.sections.archive}
				title='Архив'
				description='Архивированные упражнения и программы с проверкой актуальной версии, прямых ссылок и permission-aware восстановлением.'
			/>

			<Card>
				<CardContent className='space-y-4 p-4 sm:p-5'>
					<div className='flex flex-wrap gap-2' role='tablist' aria-label='Тип архивных объектов'>
						<Button ref={exerciseTabRef} id='archive-tab-exercises' type='button' variant={filters.kind === 'exercises' ? 'default' : 'outline'} onClick={() => changeKind('exercises')} onKeyDown={event => handleTabKeyDown(event, 'exercises')} role='tab' aria-selected={filters.kind === 'exercises'} aria-controls='archive-panel-exercises' tabIndex={filters.kind === 'exercises' ? 0 : -1}>
							<Dumbbell className='h-4 w-4' aria-hidden='true' /> Упражнения
						</Button>
						<Button ref={programTabRef} id='archive-tab-programs' type='button' variant={filters.kind === 'programs' ? 'default' : 'outline'} onClick={() => changeKind('programs')} onKeyDown={event => handleTabKeyDown(event, 'programs')} role='tab' aria-selected={filters.kind === 'programs'} aria-controls='archive-panel-programs' tabIndex={filters.kind === 'programs' ? 0 : -1}>
							<Layers3 className='h-4 w-4' aria-hidden='true' /> Программы
						</Button>
					</div>
					<form onSubmit={submitSearch} className='flex flex-col gap-2 sm:flex-row'>
						<div className='relative flex-1'>
							<Search className='pointer-events-none absolute left-3 top-3 h-4 w-4 text-muted-foreground' />
							<Input aria-label={filters.kind === 'exercises' ? 'Поиск архивных упражнений' : 'Поиск архивных программ'} value={searchDraft} onChange={event => setSearchDraft(event.target.value)} placeholder={filters.kind === 'exercises' ? 'Поиск архивных упражнений' : 'Поиск архивных программ'} className='pl-9' />
						</div>
						<Button type='submit' variant='outline'>Найти</Button>
						{hasFilters ? <Button type='button' variant='ghost' onClick={resetFilters}><X className='h-4 w-4' /> Сбросить</Button> : null}
					</form>
					<div className='grid gap-3 sm:grid-cols-3'>
						<select value={filters.sort} onChange={event => updateFilters({ sort: event.target.value as ArchiveFilters['sort'] })} className='h-10 rounded-md border bg-background px-3 text-sm' aria-label='Поле сортировки'>
							<option value='updated_at'>По дате изменения</option>
							<option value='name'>По названию</option>
							<option value='id'>По идентификатору</option>
						</select>
						<select value={filters.direction} onChange={event => updateFilters({ direction: event.target.value as ArchiveFilters['direction'] })} className='h-10 rounded-md border bg-background px-3 text-sm' aria-label='Направление сортировки'>
							<option value='desc'>По убыванию</option>
							<option value='asc'>По возрастанию</option>
						</select>
						<select value={filters.limit} onChange={event => updateFilters({ limit: Number(event.target.value) })} className='h-10 rounded-md border bg-background px-3 text-sm' aria-label='Размер страницы'>
							{[10, 25, 50, 100].map(limit => <option key={limit} value={limit}>{limit} на странице</option>)}
						</select>
					</div>
				</CardContent>
			</Card>

			{activeQuery.isPending ? <LoadingState label='Загрузка архива…' /> : null}
			{activeQuery.isError ? <ErrorState error={activeQuery.error} onRetry={() => void activeQuery.refetch()} /> : null}
			{!activeQuery.isPending && !activeQuery.isError && count === 0 ? (
				<EmptyState title='Архив пуст' description={filters.search ? 'По текущему поиску архивные объекты не найдены.' : 'Архивированные объекты появятся здесь после изменения статуса.'} action={hasFilters ? <Button variant='outline' onClick={resetFilters}>Сбросить фильтры</Button> : undefined} />
			) : null}

			{filters.kind === 'exercises' && exercises.data?.items.length ? (
				<Card id='archive-panel-exercises' role='tabpanel' aria-labelledby='archive-tab-exercises' className='overflow-hidden'>
					<div className='overflow-x-auto'>
						<table className='w-full min-w-[900px] text-left text-sm'>
							<caption className='sr-only'>Архивированные упражнения</caption>
							<thead className='border-b bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground'><tr><th scope='col' className='px-4 py-3' aria-sort={filters.sort === 'name' ? (filters.direction === 'asc' ? 'ascending' : 'descending') : 'none'}>Упражнение</th><th scope='col' className='px-4 py-3'>Связи</th><th scope='col' className='px-4 py-3' aria-sort={filters.sort === 'updated_at' ? (filters.direction === 'asc' ? 'ascending' : 'descending') : 'none'}>Изменено</th><th scope='col' className='px-4 py-3 text-right'>Действия</th></tr></thead>
							<tbody className='divide-y'>{exercises.data.items.map(exercise => (
								<tr key={exercise.id} className='hover:bg-muted/30'>
									<td className='px-4 py-3'><p className='font-medium'>{exercise.name}</p><p className='text-xs text-muted-foreground'>ID {exercise.id} · v{exercise.version}</p></td>
									<td className='px-4 py-3'><p>{exercise.media?.length ?? 0} медиа · {exercise.muscles?.length ?? 0} мышц</p><p className='mt-1 flex items-center gap-1 text-xs text-amber-700'><AlertTriangle className='h-3.5 w-3.5' /> Прямые ссылки программ проверяются перед восстановлением</p></td>
									<td className='px-4 py-3 text-muted-foreground'>{formatDate(exercise.updated_at)}</td>
									<td className='px-4 py-3'><div className='flex justify-end gap-2'><Button variant='ghost' size='sm' onClick={() => history.push(`/exercises?lifecycle=ARCHIVED&exercise=${exercise.id}`)}><ExternalLink className='h-4 w-4' /> Открыть</Button>{can('restoreExercise') ? <Button size='sm' onClick={() => openRestore({ kind: 'exercises', item: exercise })}><ArchiveRestore className='h-4 w-4' /> Восстановить</Button> : null}</div></td>
								</tr>
							))}</tbody>
						</table>
					</div>
				</Card>
			) : null}

			{filters.kind === 'programs' && programs.data?.items.length ? (
				<Card id='archive-panel-programs' role='tabpanel' aria-labelledby='archive-tab-programs' className='overflow-hidden'>
					<div className='overflow-x-auto'>
						<table className='w-full min-w-[900px] text-left text-sm'>
							<caption className='sr-only'>Архивированные программы</caption>
							<thead className='border-b bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground'><tr><th scope='col' className='px-4 py-3' aria-sort={filters.sort === 'name' ? (filters.direction === 'asc' ? 'ascending' : 'descending') : 'none'}>Программа</th><th scope='col' className='px-4 py-3'>Структура и ссылки</th><th scope='col' className='px-4 py-3' aria-sort={filters.sort === 'updated_at' ? (filters.direction === 'asc' ? 'ascending' : 'descending') : 'none'}>Изменено</th><th scope='col' className='px-4 py-3 text-right'>Действия</th></tr></thead>
							<tbody className='divide-y'>{programs.data.items.map(program => {
								const structure = getProgramStructure(program)
								const references = programReferenceSummary(program)
								return <tr key={program.id} className='hover:bg-muted/30'>
									<td className='px-4 py-3'><p className='font-medium'>{program.name}</p><p className='text-xs text-muted-foreground'>ID {program.id} · v{program.version}</p></td>
									<td className='px-4 py-3'><p>{structure.weeks} нед. · {structure.trainingDays} трен. · {structure.restDays} отдых</p><p className='mt-1 text-xs text-amber-700'>{references.workouts} шаблонов тренировок · {references.exercises} упражнений; пользовательская история не изменяется</p></td>
									<td className='px-4 py-3 text-muted-foreground'>{formatDate(program.updated_at)}</td>
									<td className='px-4 py-3'><div className='flex justify-end gap-2'><Button variant='ghost' size='sm' onClick={() => history.push(`/programs/${program.id}?lifecycle=ARCHIVED`)}><ExternalLink className='h-4 w-4' /> Открыть</Button>{can('restoreProgram') ? <Button size='sm' onClick={() => openRestore({ kind: 'programs', item: program })}><ArchiveRestore className='h-4 w-4' /> Восстановить</Button> : null}</div></td>
								</tr>
							})}</tbody>
						</table>
					</div>
				</Card>
			) : null}

			{count > 0 ? <div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'><p className='text-sm text-muted-foreground' role='status' aria-live='polite'>Показано {count} из {activePage?.total ?? count}</p><div className='flex gap-2'><Button variant='outline' size='sm' disabled={!cursorHistory.length || activeQuery.isFetching} onClick={goPrevious}><ChevronLeft className='h-4 w-4' /> Назад</Button><Button variant='outline' size='sm' disabled={!getNextCursor(activePage) || activeQuery.isFetching} onClick={goNext}>Далее <ChevronRight className='h-4 w-4' /></Button></div></div> : null}

			<ConfirmDialog
				open={restoreTarget !== null}
				onOpenChange={open => { if (!open) closeRestore() }}
				title={restoreTarget ? `Восстановить «${targetName(restoreTarget)}»?` : 'Восстановить объект?'}
				description='Объект вернётся в состояние черновика. Публикация потребует отдельной серверной проверки.'
				confirmLabel={conflictMessage ? 'Повторить с новым ETag' : 'Восстановить'}
				isPending={restoreMutation.isPending}
				onConfirm={() => restoreMutation.mutate()}>
				{restoreTarget ? <div className='space-y-3 rounded-lg border bg-muted/30 p-4 text-sm'>
					<p><span className='text-muted-foreground'>Версия:</span> v{restoreTarget.item.version} · <code>{restoreTarget.item.etag}</code></p>
					{restoreTarget.kind === 'programs' ? (() => {
						const refs = programReferenceSummary(restoreTarget.item)
						return <p className='flex gap-2 text-amber-800'><AlertTriangle className='mt-0.5 h-4 w-4 shrink-0' /> Программа сохраняет ссылки на {refs.workouts} шаблонов тренировок и {refs.exercises} упражнений. Записи на программы, избранное и история пользователей не изменяются этой командой.</p>
					})() : exerciseReferences.isPending ? <p>Проверяем прямые ссылки из структур программ…</p> : exerciseReferences.isError ? <p className='flex gap-2 text-amber-800'><AlertTriangle className='mt-0.5 h-4 w-4 shrink-0' /> Не удалось проверить прямые ссылки программ. Восстановление допустимо, но проверка перед публикацией остаётся обязательной.</p> : exerciseReferences.data.count ? <div className='space-y-2 text-amber-800'><p className='flex gap-2'><AlertTriangle className='mt-0.5 h-4 w-4 shrink-0' /> Упражнение используется в {exerciseReferences.data.count} текущих структур программ:</p><ul className='list-disc space-y-1 pl-6'>{exerciseReferences.data.programs.slice(0, 8).map(program => <li key={program.id}>{program.name} · {programLifecycleLabel[program.lifecycle]}</li>)}</ul>{exerciseReferences.data.count > 8 ? <p>И ещё {exerciseReferences.data.count - 8}.</p> : null}</div> : <p>Прямых ссылок из текущих структур программ не найдено. Исторические записи тренировок этим контрактом не перечисляются и не изменяются.</p>}
					{conflictMessage ? <p className='rounded-md border border-amber-300 bg-amber-50 p-3 text-amber-900'>{conflictMessage}</p> : null}
				</div> : null}
			</ConfirmDialog>
		</section>
	)
}
